import { useState } from 'react';
import { MessageSquarePlus, X, Loader2, CheckCircle, Send } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';

const CATEGORIES = [
  { key: 'health', label: 'Health & Care' },
  { key: 'property', label: 'Property' },
  { key: 'carers', label: 'Carers' },
  { key: 'elderly', label: 'Elderly Portal' },
  { key: 'billing', label: 'Billing & Plans' },
  { key: 'general', label: 'General' },
];

// Where the trigger floats: `place` is chosen in Layout.js, which draws what
// the trigger has to stay clear of. From lg up it is the bottom right corner
// for everyone. Below lg a family account has the bottom nav in that corner
// and, on most pages, the assistant button above the nav: the trigger goes
// above whichever of the two is highest (scripts/floating-buttons.test.js
// does the sums). It stays at right-5 throughout, which centres it on the
// assistant button while it is a round icon (below sm).
const TRIGGER_BOTTOM = {
  corner: 'bottom-5',
  aboveNav: 'bottom-24 lg:bottom-5',
  aboveAssistant: 'bottom-[164px] lg:bottom-5',
};

export default function FeedbackWidget({ place = 'corner' }) {
  const { user, api } = useAuth();
  const [open, setOpen] = useState(false);
  const [categories, setCategories] = useState([]);
  const [text, setText] = useState('');
  const [email, setEmail] = useState(user?.email || '');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const toggleCat = (key) => {
    setCategories(prev => prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]);
  };

  const handleSubmit = async () => {
    if (!text || text.trim().length < 10) {
      toast.error('Please describe your feedback (at least 10 characters)');
      return;
    }
    setSubmitting(true);
    try {
      await api.post('/feedback', {
        categories,
        feature_request: text.trim(),
        email,
        user_id: user?.id,
        source_page: window.location.pathname,
      });
      setSubmitted(true);
      setTimeout(() => { setOpen(false); setSubmitted(false); setText(''); setCategories([]); }, 2500);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit');
    }
    setSubmitting(false);
  };

  return (
    <>
      {/* Floating trigger. z-40, with the assistant button and the bottom nav:
          a page's dialog (z-50) covers all three. */}
      <button
        onClick={() => setOpen(true)}
        data-testid="feedback-trigger"
        className={`fixed ${TRIGGER_BOTTOM[place] || TRIGGER_BOTTOM.corner} right-5 z-40 flex items-center gap-2 px-4 py-2.5 rounded-full shadow-lg transition-all hover:scale-105`}
        style={{ background: '#1F2937', color: 'white', display: open ? 'none' : 'flex' }}
      >
        <MessageSquarePlus size={16} />
        <span className="text-xs font-semibold hidden sm:inline">Feedback</span>
      </button>

      {/* Panel */}
      {open && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center" data-testid="feedback-panel">
          <div className="absolute inset-0 bg-black/30" onClick={() => setOpen(false)} />
          <div className="relative w-full max-w-md mx-4 mb-4 sm:mb-0 rounded-2xl bg-white shadow-2xl overflow-hidden animate-fade-in-up"
            style={{ maxHeight: '85vh' }}>

            {/* Header */}
            <div className="flex items-center justify-between p-4" style={{ borderBottom: '1px solid var(--border-color)' }}>
              <div>
                <h3 className="text-base font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>Share Your Feedback</h3>
                <p className="text-xs" style={{ color: 'var(--text-muted)' }}>What feature would you like to see?</p>
              </div>
              <button onClick={() => setOpen(false)} className="p-1" aria-label="Close feedback"><X size={18} style={{ color: 'var(--text-light)' }} /></button>
            </div>

            {submitted ? (
              <div className="p-8 text-center" data-testid="feedback-success">
                <CheckCircle size={40} className="mx-auto mb-4" style={{ color: 'var(--success)' }} />
                <p className="text-base font-semibold mb-1">Thank you!</p>
                <p className="text-sm" style={{ color: 'var(--text-muted)' }}>Your feedback has been recorded.</p>
              </div>
            ) : (
              <div className="p-4 space-y-4 overflow-y-auto" style={{ maxHeight: '60vh' }}>
                {/* Categories */}
                <div>
                  <p className="text-xs font-semibold mb-2" style={{ color: 'var(--text-muted)' }}>Category (optional)</p>
                  <div className="flex flex-wrap gap-2">
                    {CATEGORIES.map(c => (
                      <button
                        key={c.key}
                        onClick={() => toggleCat(c.key)}
                        className="px-3 py-1.5 rounded-full text-xs font-medium transition-all"
                        style={{
                          background: categories.includes(c.key) ? 'var(--brand-primary)' : 'var(--bg-main)',
                          color: categories.includes(c.key) ? 'white' : 'var(--text-muted)',
                          border: `1px solid ${categories.includes(c.key) ? 'var(--brand-primary)' : 'var(--border-color)'}`,
                        }}
                        data-testid={`feedback-cat-${c.key}`}
                      >
                        {c.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Text area */}
                <div>
                  <p className="text-xs font-semibold mb-2" style={{ color: 'var(--text-muted)' }}>Describe the feature you'd like</p>
                  <textarea
                    value={text}
                    onChange={e => setText(e.target.value)}
                    placeholder="I'd love to see..."
                    rows={4}
                    className="nsofar-input text-sm"
                    style={{ height: 'auto', resize: 'none', padding: '12px 16px' }}
                    data-testid="feedback-text"
                  />
                </div>

                {/* Email */}
                {!user && (
                  <input
                    type="email"
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    placeholder="Your email (optional)"
                    className="nsofar-input text-sm"
                    data-testid="feedback-email"
                  />
                )}

                {/* Submit */}
                <button
                  onClick={handleSubmit}
                  disabled={submitting}
                  className="nsofar-btn nsofar-btn-primary w-full flex items-center justify-center gap-2"
                  data-testid="feedback-submit"
                >
                  {submitting ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
                  {submitting ? 'Sending...' : 'Submit Feedback'}
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}
